import React, { useEffect, useState } from "react";
import { api, errMsg } from "../lib/api";
import { Card, PageHeader, Btn, Input, Select, Textarea, Pill } from "../components/UI";
import { ScanMap } from "./dm/ScanMap";
import { toast } from "sonner";
import { Plus, Trash2, QrCode, Printer, Send, RefreshCw, Loader2, Download, MapPin, Map as MapIcon } from "lucide-react";

const FORMATS = [
  { key: "postcard_4x6", label: 'Postcard 4"×6"' },
  { key: "postcard_6x9", label: 'Postcard 6"×9"' },
  { key: "letter", label: 'Letter 8.5"×11"' },
];

const STATUS_STYLES = { draft: "Draft", submitted: "Pending", in_production: "Active", mailed: "Active", delivered: "Active" };

const EMPTY = {
  name: "", format: "postcard_4x6", entity_id: "", headline: "", message: "",
  cta_label: "Learn more", landing_url: "", quantity: 1000, audience_note: "", mail_list_id: "",
  primary_color: "#120f0d", accent_color: "#fbbf24", disclaimer: "",
};

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const splitCsvLine = (l) => l.split(",").map(s => s.replace(/^"|"$/g, "").trim());

const parseAddressCsv = (text) => {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) return [];
  const header = splitCsvLine(lines[0]).map(h => h.toLowerCase().replace(/[^a-z0-9]/g, ""));
  const find = (...cands) => header.findIndex(h => cands.includes(h));
  const iName = find("name", "fullname", "recipient");
  const iA1 = find("address1", "address", "street", "streetaddress");
  const iA2 = find("address2", "unit", "apt", "suite");
  const iCity = find("city", "town");
  const iState = find("state", "st");
  const iZip = find("zip", "zipcode", "postalcode", "postal");
  const out = [];
  for (const line of lines.slice(1)) {
    const c = splitCsvLine(line);
    const rec = {
      name: iName >= 0 ? c[iName] || "" : "",
      address1: iA1 >= 0 ? c[iA1] || "" : "",
      address2: iA2 >= 0 ? c[iA2] || "" : "",
      city: iCity >= 0 ? c[iCity] || "" : "",
      state: iState >= 0 ? c[iState] || "" : "",
      zip: iZip >= 0 ? c[iZip] || "" : "",
    };
    if (rec.address1 && rec.zip) out.push(rec);
  }
  return out;
};

export default function DirectMail() {
  const [mailers, setMailers] = useState([]);
  const [entities, setEntities] = useState([]);
  const [mailLists, setMailLists] = useState([]);
  const [editing, setEditing] = useState(null);
  const [artwork, setArtwork] = useState([]);
  const [reports, setReports] = useState({});
  const [busy, setBusy] = useState("");
  const [mlName, setMlName] = useState("");
  const [mlRecipients, setMlRecipients] = useState([]);
  const [showMap, setShowMap] = useState(false);

  const load = async () => {
    api.get("/direct-mail/campaigns").then(r => setMailers(r.data)).catch(() => {});
    api.get("/direct-mail/lists").then(r => setMailLists(r.data)).catch(() => {});
    api.get("/political-entities").then(r => setEntities(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const err = (e, fb) => toast.error(errMsg(e, fb));
  const set = (k, v) => setEditing(f => ({ ...f, [k]: v }));

  const save = async (silent = false) => {
    try {
      let saved;
      const payload = { ...editing, mail_list_id: editing.mail_list_id || null };
      if (editing.id) saved = (await api.put(`/direct-mail/campaigns/${editing.id}`, payload)).data;
      else saved = (await api.post("/direct-mail/campaigns", payload)).data;
      setEditing(saved);
      if (!silent) toast.success("Saved");
      load();
      return saved;
    } catch (e) { err(e, "Could not save"); return null; }
  };

  const genArtwork = async () => {
    const saved = await save(true);
    if (!saved) return;
    setBusy("art");
    try {
      const { data } = await api.post(`/direct-mail/campaigns/${saved.id}/artwork`);
      setArtwork(data.files);
      toast.success("Print-ready artwork generated");
    } catch (e) { err(e, "Artwork failed"); }
    finally { setBusy(""); }
  };

  const submit = async () => {
    const saved = await save(true);
    if (!saved) return;
    if (!window.confirm(`Submit ${saved.quantity?.toLocaleString()} pieces for fulfillment?`)) return;
    setBusy("submit");
    try {
      const { data } = await api.post(`/direct-mail/campaigns/${saved.id}/submit`);
      setEditing(data);
      toast.success("Order submitted — our team takes it from here");
      load();
    } catch (e) { err(e, "Submit failed"); }
    finally { setBusy(""); }
  };

  const fetchReport = async (id) => {
    try {
      const { data } = await api.get(`/direct-mail/campaigns/${id}/report`);
      setReports(r => ({ ...r, [id]: data }));
    } catch { /* noop */ }
  };

  const onAddressCsv = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setMlRecipients(parseAddressCsv(String(reader.result || "")));
    reader.readAsText(file);
  };

  const createMailList = async () => {
    setBusy("mlist");
    try {
      await api.post("/direct-mail/lists", { name: mlName, recipients: mlRecipients });
      toast.success("Mail list created");
      setMlName(""); setMlRecipients([]);
      load();
    } catch (e) { err(e, "Could not create mail list"); }
    finally { setBusy(""); }
  };

  const downloadList = async (l) => {
    try {
      const res = await api.get(`/direct-mail/lists/${l.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${l.name}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { err(e, "Download failed"); }
  };

  const fmt = FORMATS.find(f => f.key === editing?.format) || FORMATS[0];
  const isPostcard = editing?.format !== "letter";
  const attachedList = editing && mailLists.find(l => l.id === editing.mail_list_id);

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        eyebrow="Direct Mail Channel"
        title="Programmatic Direct Mail"
        subtitle="Design postcards and letters, track them like digital with QR attribution, and let TotalReach handle print and postage."
        action={!editing && (
          <div className="flex gap-2">
            <Btn variant="secondary" onClick={() => setShowMap(v => !v)} data-testid="dm-heatmap-btn">
              <MapIcon className="w-4 h-4" /> Scan heatmap
            </Btn>
            <Btn onClick={() => { setEditing({ ...EMPTY }); setArtwork([]); }} data-testid="new-dm-btn"><Plus className="w-4 h-4" /> New mailer</Btn>
          </div>
        )}
      />

      {!editing && showMap && <ScanMap />}

      {editing && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <Card data-testid="dm-editor">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-bold">{editing.id ? "Edit mailer" : "New mailer"}</h3>
              <button onClick={() => setEditing(null)} className="text-xs text-slate-400 hover:text-white">← Back to list</button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Name" value={editing.name} onChange={e => set("name", e.target.value)} className="col-span-2" data-testid="dm-name-input" />
              <Select label="Format" value={editing.format} onChange={e => set("format", e.target.value)} data-testid="dm-format-select">
                {FORMATS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
              </Select>
              <Select label="Committee (optional)" value={editing.entity_id || ""} onChange={e => {
                const ent = entities.find(x => x.id === e.target.value);
                setEditing(f => ({ ...f, entity_id: e.target.value, disclaimer: f.disclaimer || (ent?.approved_disclaimers?.[0] || "") }));
              }}>
                <option value="">None (non-political)</option>
                {entities.map(en => <option key={en.id} value={en.id}>{en.public_campaign_name || en.legal_org_name}</option>)}
              </Select>
              <Input label="Headline" value={editing.headline} onChange={e => set("headline", e.target.value)} className="col-span-2" data-testid="dm-headline-input" />
              <Textarea label="Message" rows={4} value={editing.message} onChange={e => set("message", e.target.value)} className="col-span-2" />
              <Input label="CTA label" value={editing.cta_label} onChange={e => set("cta_label", e.target.value)} />
              <Input label="Landing URL (QR destination)" value={editing.landing_url} onChange={e => set("landing_url", e.target.value)} data-testid="dm-landing-input" />
              <Select label="Mailing list (optional)" value={editing.mail_list_id || ""} onChange={e => {
                const lst = mailLists.find(l => l.id === e.target.value);
                setEditing(f => ({ ...f, mail_list_id: e.target.value, quantity: lst ? lst.count : f.quantity }));
              }} data-testid="dm-mail-list-select">
                <option value="">None — set quantity manually</option>
                {mailLists.map(l => <option key={l.id} value={l.id}>{l.name} ({l.count} addresses)</option>)}
              </Select>
              <Input label={attachedList ? "Quantity (from list)" : "Quantity (pieces)"} type="number" value={editing.quantity}
                disabled={!!editing.mail_list_id}
                onChange={e => set("quantity", parseInt(e.target.value) || 0)} data-testid="dm-quantity-input" />
              <Input label="Audience note (who gets it)" value={editing.audience_note} onChange={e => set("audience_note", e.target.value)} className="col-span-2" />
              <Input label="Primary color" type="color" value={editing.primary_color} onChange={e => set("primary_color", e.target.value)} />
              <Input label="Accent color" type="color" value={editing.accent_color} onChange={e => set("accent_color", e.target.value)} />
              <Input label="Disclaimer (required for political)" value={editing.disclaimer} onChange={e => set("disclaimer", e.target.value)} className="col-span-2" data-testid="dm-disclaimer-input" />
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Btn variant="secondary" onClick={() => save()} data-testid="dm-save-btn">Save draft</Btn>
              <Btn variant="secondary" onClick={genArtwork} disabled={busy === "art"} data-testid="dm-artwork-btn">
                {busy === "art" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />} Print-ready artwork
              </Btn>
              {attachedList && (
                <Btn variant="secondary" onClick={() => downloadList(attachedList)} data-testid="dm-download-recipients-btn">
                  <Download className="w-4 h-4" /> Recipient file
                </Btn>
              )}
              <Btn onClick={submit} disabled={busy === "submit" || editing.status === "submitted"} data-testid="dm-submit-btn" className="ml-auto">
                <Send className="w-4 h-4" /> {editing.status === "submitted" ? "Submitted" : "Submit order"}
              </Btn>
            </div>
            {artwork.length > 0 && (
              <div className="mt-4 pt-4 border-t border-white/10 flex flex-wrap gap-4" data-testid="dm-artwork-files">
                {artwork.map(f => (
                  <a key={f.name} href={`${API_BASE}${f.url}`} target="_blank" rel="noreferrer" className="block group">
                    <img src={`${API_BASE}${f.url}`} alt={f.name} className="h-32 rounded-md border border-white/10 group-hover:border-cyan-400/50 transition-colors" />
                    <div className="text-[10px] text-slate-400 mt-1 text-center">{f.name} · 300 DPI</div>
                  </a>
                ))}
              </div>
            )}
            <div className="mt-4 text-[10px] text-slate-500">Fulfillment: TotalReach Managed — we handle print and postage. Provider API integration activates automatically once available.</div>
          </Card>

          {/* Live preview */}
          <Card data-testid="dm-preview">
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-3">Live preview — {fmt.label}</div>
            <div className="rounded-lg overflow-hidden border border-white/10 relative" style={{
              aspectRatio: isPostcard ? "3/2" : "17/22",
              background: isPostcard ? editing.primary_color : "#ffffff",
            }}>
              {isPostcard ? (
                <div className="absolute inset-0 p-[6%] flex flex-col">
                  <div className="absolute top-0 left-0 right-0 h-[2%]" style={{ background: editing.accent_color }} />
                  <div className="font-display font-bold text-white leading-tight" style={{ fontSize: "clamp(18px,3.4vw,34px)", maxWidth: "70%" }}>{editing.headline || "Your headline here"}</div>
                  <div className="text-slate-300 mt-3" style={{ fontSize: "clamp(10px,1.4vw,14px)", maxWidth: "60%" }}>{editing.message || "Your message to voters…"}</div>
                  <div className="mt-auto flex items-end justify-between">
                    <span className="rounded-full font-semibold text-slate-900 px-4 py-2" style={{ background: editing.accent_color, fontSize: "clamp(10px,1.5vw,14px)" }}>{editing.cta_label || "Learn more"}</span>
                    {editing.qr_data_uri
                      ? <img src={editing.qr_data_uri} alt="QR" className="w-[18%] bg-paper p-1 rounded" />
                      : <div className="w-[18%] aspect-square bg-white/90 rounded flex items-center justify-center"><QrCode className="w-1/2 h-1/2 text-slate-900" /></div>}
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-black/85 text-slate-300 px-[6%] py-1 truncate" style={{ fontSize: "clamp(7px,1vw,10px)" }}>{editing.disclaimer || "Paid for by …"}</div>
                </div>
              ) : (
                <div className="absolute inset-0 flex flex-col text-[#1F2937]">
                  <div className="px-[9%] py-[3%] text-white font-display font-bold" style={{ background: editing.primary_color, fontSize: "clamp(12px,1.8vw,18px)" }}>{editing.name || "Campaign Letter"}</div>
                  <div className="px-[9%] pt-[4%] text-sm">Dear Neighbor,</div>
                  <div className="px-[9%] pt-[2%] font-bold" style={{ fontSize: "clamp(12px,1.6vw,16px)" }}>{editing.headline || "Your headline"}</div>
                  <div className="px-[9%] pt-[2%] text-xs leading-relaxed whitespace-pre-wrap flex-1 overflow-hidden">{editing.message || "Your letter body…"}</div>
                  <div className="px-[9%] pb-[4%] flex items-center gap-3">
                    {editing.qr_data_uri && <img src={editing.qr_data_uri} alt="QR" className="w-14 h-14" />}
                    <div className="text-[9px] text-slate-500">{editing.disclaimer || "Paid for by …"}</div>
                  </div>
                </div>
              )}
            </div>
            <div className="text-[10px] text-slate-500 mt-3 flex items-center gap-1.5">
              <QrCode className="w-3.5 h-3.5 text-cyan-300" />
              Every piece carries a unique QR code — scans are tracked like clicks and attributed with UTM parameters{editing.scan_url ? <>: <span className="font-mono text-slate-400">{editing.scan_url}</span></> : "."}
            </div>
          </Card>
        </div>
      )}

      {!editing && (
        <div className="space-y-3" data-testid="dm-list">
          {mailers.map(m => {
            const rep = reports[m.id];
            return (
              <Card key={m.id}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3">
                    {m.qr_data_uri && <img src={m.qr_data_uri} alt="QR" className="w-12 h-12 rounded bg-paper p-0.5" />}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-display font-bold text-white">{m.name}</span>
                        <Pill status={STATUS_STYLES[m.status] || "Draft"}>{m.status.replace(/_/g, " ")}</Pill>
                      </div>
                      <div className="text-xs text-slate-400 mt-1">{FORMATS.find(f => f.key === m.format)?.label} · {Number(m.quantity || 0).toLocaleString()} pieces · <span className="text-cyan-300">{m.scans || 0} QR scans</span></div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Btn variant="secondary" onClick={() => fetchReport(m.id)} data-testid={`dm-report-btn-${m.id}`}><RefreshCw className="w-3.5 h-3.5" /> Scans</Btn>
                    <Btn variant="secondary" onClick={() => { setEditing(m); setArtwork((m.artwork_files || []).map(f => ({ name: f, url: `/api/public/dm-assets/${m.id}/${f}` }))); }} data-testid={`dm-edit-${m.id}`}>Open</Btn>
                    <button onClick={async () => { if (window.confirm("Delete this mailer?")) { await api.delete(`/direct-mail/campaigns/${m.id}`); load(); } }}
                      className="text-slate-500 hover:text-rose-300 p-1.5"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                {rep && (
                  <div className="mt-3 pt-3 border-t border-white/5 flex flex-wrap gap-6 text-center" data-testid={`dm-stats-${m.id}`}>
                    <div><div className="text-[10px] uppercase tracking-wider text-slate-500">QR scans</div><div className="font-display text-lg font-bold text-white">{rep.scans}</div></div>
                    <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Scan rate</div><div className="font-display text-lg font-bold text-emerald-300">{rep.scan_rate}%</div></div>
                    <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Pieces</div><div className="font-display text-lg font-bold text-white">{Number(rep.quantity).toLocaleString()}</div></div>
                    {(rep.by_day || []).slice(-5).map(d => (
                      <div key={d.date}><div className="text-[10px] uppercase tracking-wider text-slate-500">{d.date.slice(5)}</div><div className="font-display text-lg font-bold text-cyan-300">{d.scans}</div></div>
                    ))}
                  </div>
                )}
              </Card>
            );
          })}
          {mailers.length === 0 && <div className="text-center py-14 text-sm text-slate-500">No mailers yet — design your first postcard.</div>}
        </div>
      )}

      {/* Mailing lists */}
      {!editing && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="dm-mail-lists-card">
          <Card>
            <h3 className="font-display text-lg font-bold mb-1 flex items-center gap-2"><MapPin className="w-4 h-4 text-cyan-300" /> New mailing list</h3>
            <p className="text-xs text-slate-500 mb-3">Upload a CSV of mailing addresses (name, address, city, state, zip). Attaching a list to a mailer sets its quantity automatically.</p>
            <Input label="List name" value={mlName} onChange={e => setMlName(e.target.value)} data-testid="dm-list-name" />
            <div className="mt-3">
              <input type="file" accept=".csv,.txt" onChange={onAddressCsv} data-testid="dm-list-file"
                className="block w-full text-xs text-slate-400 file:mr-3 file:rounded-full file:border-0 file:bg-cyan-400/15 file:text-cyan-300 file:px-4 file:py-2 file:text-xs" />
              {mlRecipients.length > 0 && <div className="text-xs text-emerald-300 mt-2" data-testid="dm-list-parsed">{mlRecipients.length} valid addresses parsed</div>}
            </div>
            <Btn className="mt-4" onClick={createMailList} disabled={busy === "mlist" || !mlRecipients.length} data-testid="dm-list-create-btn">Create mail list</Btn>
          </Card>
          <Card padded={false}>
            <div className="px-5 py-3 border-b border-white/10 font-display font-bold">Your mailing lists</div>
            <div className="divide-y divide-white/5">
              {mailLists.map(l => (
                <div key={l.id} className="px-5 py-3 flex items-center justify-between text-sm">
                  <div><span className="text-white">{l.name}</span> <span className="text-slate-500 text-xs">· {l.count} addresses</span></div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => downloadList(l)} className="text-cyan-300 hover:text-cyan-100 p-1" data-testid={`dm-list-download-${l.id}`}><Download className="w-4 h-4" /></button>
                    <button onClick={async () => { await api.delete(`/direct-mail/lists/${l.id}`); load(); }} className="text-slate-500 hover:text-rose-300 p-1"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
              {mailLists.length === 0 && <div className="px-5 py-8 text-center text-sm text-slate-500">No mailing lists yet.</div>}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
